import type { ConnectorId, ConnectorSnapshot, ConnectorStatus } from "./connectors/types";
import { systems, type SystemId } from "./systems";
import type { TimelineEvidenceEvent } from "./timeline-types";

export type SimulationModelId =
  | "btc-jump-diffusion"
  | "financial-regime-transition"
  | "institutional-rule-transition"
  | "strategic-interaction-network";

export type SimulationKind =
  | "Stochastic process"
  | "Regime transition"
  | "Institutional transition"
  | "Interaction network";

export type SimulationCheckpoint = {
  offsetDays: number;
  kind: "State estimate" | "Transition test" | "Branch condition" | "Terminal classification";
  title: string;
  detail: string;
  mechanism: string;
  stateVariables: string[];
};

export type SimulationOutcomeDefinition = {
  id: string;
  title: string;
  summary: string;
  priorWeight: number;
  modelGap?: boolean;
  supportingConditions: string[];
  invalidatingConditions: string[];
  checkpoints: SimulationCheckpoint[];
};

export type SimulationParameter = {
  symbol: string;
  label: string;
  value: string;
  provenance: "fixed baseline" | "source conditioned" | "derived";
};

export type SimulationModel = {
  id: SimulationModelId;
  name: string;
  kind: SimulationKind;
  version: string;
  purpose: string;
  target: string;
  partitionRule: string;
  horizonDays: number;
  timeStep: string;
  particleCount: number;
  method: string;
  equation: string;
  systemIds: SystemId[];
  requiredSourceIds: ConnectorId[];
  sourceGaps: string[];
  stateVariables: string[];
  parameters: SimulationParameter[];
  sharedCheckpoints: SimulationCheckpoint[];
  outcomes: SimulationOutcomeDefinition[];
  limitations: string[];
};

export type SimulationOutcomeResult = {
  id: string;
  title: string;
  sampleCount: number;
  probabilityBps: number;
};

export type SimulationMetric = {
  label: string;
  value: number;
  unit: "%" | "USD";
};

export type SimulationRun = {
  id: string;
  modelId: SimulationModelId;
  modelVersion: string;
  asOf: string | null;
  revision: number;
  seed: number;
  status: "completed" | "input limited";
  particleCount: number;
  inputCoverage: number;
  modelConfidence: number;
  calibration: {
    status: "pending";
    outOfSampleForecasts: 0;
    brierScore: null;
    logLoss: null;
  };
  inputs: Array<{
    label: string;
    value: string;
    sourceId?: ConnectorId;
  }>;
  outcomes: SimulationOutcomeResult[];
  metrics: SimulationMetric[];
  histogram?: Array<{ lower: number; upper: number; count: number }>;
};

const commonGapConditions = [
  "Observed state falls outside every defined terminal regime",
  "A missing mechanism changes the modeled boundary before the horizon",
];

const commonGapInvalidators = [
  "The terminal state can be classified under an existing regime without contradiction",
];

export const simulationModels: SimulationModel[] = [
  {
    id: "btc-jump-diffusion",
    name: "BTC jump-diffusion ensemble",
    kind: "Stochastic process",
    version: "0.1.0",
    purpose:
      "Propagate the current BTC reference state through a stochastic return process with continuous volatility and discrete jump risk.",
    target: "BTC/USD terminal 30-day return regime",
    partitionRule:
      "Every sampled terminal return enters exactly one non-overlapping return interval.",
    horizonDays: 30,
    timeStep: "1 day",
    particleCount: 20_000,
    method: "Geometric Brownian motion with Bernoulli-normal jumps and seeded Monte Carlo sampling.",
    equation: "dS/S = μ dt + σ dW + J dN",
    systemIds: ["financial"],
    requiredSourceIds: ["coinbase", "binance"],
    sourceGaps: [
      "Calibrated realized and implied volatility surfaces",
      "Order-book depth, funding, liquidations, positioning, and options skew",
    ],
    stateVariables: [
      "Spot reference",
      "Short-horizon drift",
      "Diffusive volatility",
      "Jump arrival rate",
      "Jump magnitude",
    ],
    parameters: [
      { symbol: "μ", label: "Annual drift", value: "24h return conditioned; ±30% cap", provenance: "source conditioned" },
      { symbol: "σ", label: "Annual volatility", value: "65%", provenance: "fixed baseline" },
      { symbol: "λ", label: "Daily jump probability", value: "2%", provenance: "fixed baseline" },
      { symbol: "σJ", label: "Jump dispersion", value: "8%", provenance: "fixed baseline" },
    ],
    sharedCheckpoints: [
      {
        offsetDays: 0,
        kind: "State estimate",
        title: "Initialize the market state",
        detail: "Resolve the connected spot references and condition drift only when a parseable 24-hour return is present.",
        mechanism: "Source-conditioned state estimation",
        stateVariables: ["Spot reference", "Short-horizon drift"],
      },
      {
        offsetDays: 7,
        kind: "Transition test",
        title: "Propagate diffusion and jump paths",
        detail: "Advance every particle through the same declared volatility and jump process without selecting a preferred trajectory.",
        mechanism: "Stochastic state propagation",
        stateVariables: ["Path return", "Realized path volatility", "Jump count"],
      },
    ],
    outcomes: [
      {
        id: "btc-severe-downside",
        title: "Severe downside",
        summary: "Terminal return at or below −20%.",
        priorWeight: 1,
        supportingConditions: ["Terminal simulated return ≤ −20%"],
        invalidatingConditions: ["Terminal simulated return > −20%"],
        checkpoints: [{
          offsetDays: 30,
          kind: "Terminal classification",
          title: "Classify severe downside",
          detail: "Assign every path ending at or below the lower tail boundary to this terminal regime.",
          mechanism: "Exhaustive return partition",
          stateVariables: ["Terminal return", "Maximum drawdown"],
        }],
      },
      {
        id: "btc-downside",
        title: "Downside",
        summary: "Terminal return above −20% and below −5%.",
        priorWeight: 1,
        supportingConditions: ["−20% < terminal simulated return < −5%"],
        invalidatingConditions: ["Terminal return lies outside the interval"],
        checkpoints: [{
          offsetDays: 30,
          kind: "Terminal classification",
          title: "Classify downside",
          detail: "Assign paths inside the declared negative-return interval without overlap with either tail or range outcomes.",
          mechanism: "Exhaustive return partition",
          stateVariables: ["Terminal return"],
        }],
      },
      {
        id: "btc-range",
        title: "Range",
        summary: "Terminal return from −5% through +5%.",
        priorWeight: 1,
        supportingConditions: ["−5% ≤ terminal simulated return ≤ +5%"],
        invalidatingConditions: ["Terminal return leaves the interval"],
        checkpoints: [{
          offsetDays: 30,
          kind: "Terminal classification",
          title: "Classify range retention",
          detail: "Assign paths finishing inside the central return band to the range regime.",
          mechanism: "Exhaustive return partition",
          stateVariables: ["Terminal return"],
        }],
      },
      {
        id: "btc-upside",
        title: "Upside",
        summary: "Terminal return above +5% and at or below +20%.",
        priorWeight: 1,
        supportingConditions: ["+5% < terminal simulated return ≤ +20%"],
        invalidatingConditions: ["Terminal return lies outside the interval"],
        checkpoints: [{
          offsetDays: 30,
          kind: "Terminal classification",
          title: "Classify upside",
          detail: "Assign paths inside the declared positive-return interval without overlap with the upper tail.",
          mechanism: "Exhaustive return partition",
          stateVariables: ["Terminal return"],
        }],
      },
      {
        id: "btc-extreme-upside",
        title: "Extreme upside",
        summary: "Terminal return above +20%.",
        priorWeight: 1,
        supportingConditions: ["Terminal simulated return > +20%"],
        invalidatingConditions: ["Terminal simulated return ≤ +20%"],
        checkpoints: [{
          offsetDays: 30,
          kind: "Terminal classification",
          title: "Classify extreme upside",
          detail: "Assign every path beyond the upper tail boundary to this terminal regime.",
          mechanism: "Exhaustive return partition",
          stateVariables: ["Terminal return", "Maximum drawdown"],
        }],
      },
    ],
    limitations: [
      "The volatility and jump parameters are declared baselines, not fitted estimates.",
      "The model is not calibrated out of sample and does not establish investable edge.",
      "Reflexive behavior, liquidity cascades, derivatives positioning, and regime breaks are not yet modeled.",
    ],
  },
  {
    id: "financial-regime-transition",
    name: "Financial transmission regimes",
    kind: "Regime transition",
    version: "0.1.0",
    purpose:
      "Classify the dominant financial-system transmission regime at the 90-day horizon.",
    target: "Dominant financial transmission regime at day 90",
    partitionRule:
      "Terminal classification uses the dominant regime; ambiguous or omitted mechanisms enter the explicit model-gap outcome.",
    horizonDays: 90,
    timeStep: "1 day",
    particleCount: 10_000,
    method: "Categorical regime ensemble with explicit epistemic-gap mass and seeded sampling.",
    equation: "P(zₜ₊₁ | zₜ, xₜ, θ); Σ P(zᵢ) = 1",
    systemIds: ["financial", "united-states"],
    requiredSourceIds: ["coinbase", "binance", "kalshi", "polymarket", "fred", "bls"],
    sourceGaps: ["Bank funding and lending standards", "Credit, collateral, clearing, and settlement telemetry"],
    stateVariables: ["Liquidity", "Credit availability", "Risk premia", "Settlement continuity"],
    parameters: [
      { symbol: "π", label: "Regime prior", value: "Declared hypothesis weights", provenance: "fixed baseline" },
      { symbol: "g", label: "Epistemic gap", value: "Input-coverage conditioned", provenance: "derived" },
    ],
    sharedCheckpoints: [{
      offsetDays: 7,
      kind: "State estimate",
      title: "Establish the transmission baseline",
      detail: "Assimilate repeated market, inflation, labor, and expectation observations before classifying a terminal regime.",
      mechanism: "Price discovery and policy transmission",
      stateVariables: ["Liquidity", "Risk premia", "Policy expectations"],
    }],
    outcomes: [
      {
        id: "orderly-financial-transmission",
        title: "Orderly transmission",
        summary: "Liquidity and policy signals transmit without a material break in credit, market functioning, or settlement.",
        priorWeight: 42,
        supportingConditions: ["Market depth remains functional", "Credit availability does not contract materially", "Settlement continuity is preserved"],
        invalidatingConditions: ["Persistent liquidity deterioration", "Material credit contraction", "Failed or impaired settlement"],
        checkpoints: [
          { offsetDays: 30, kind: "Branch condition", title: "Test orderly repricing", detail: "Compare the baseline with market functioning and credit proxies after material data or policy updates.", mechanism: "Liquidity and credit transmission", stateVariables: ["Market functioning", "Credit availability"] },
          { offsetDays: 90, kind: "Terminal classification", title: "Retain orderly transmission", detail: "Classify this path only when no competing regime dominates the horizon state.", mechanism: "Dominant-regime classification", stateVariables: ["Liquidity", "Credit availability", "Settlement continuity"] },
        ],
      },
      {
        id: "contained-financial-stress",
        title: "Stress contained",
        summary: "A material shock occurs but remains localized or is absorbed before broader credit and settlement propagation.",
        priorWeight: 28,
        supportingConditions: ["A measurable shock occurs", "Funding and settlement remain functional", "Policy or balance-sheet absorption contains spillover"],
        invalidatingConditions: ["Stress propagates into sustained credit contraction", "No material shock occurs during the horizon"],
        checkpoints: [
          { offsetDays: 30, kind: "Branch condition", title: "Separate shock from propagation", detail: "Test whether volatility reaches funding, collateral, credit, or settlement rather than prices alone.", mechanism: "Shock absorption", stateVariables: ["Volatility", "Funding conditions", "Collateral"] },
          { offsetDays: 90, kind: "Terminal classification", title: "Resolve containment", detail: "Classify containment only when the initiating shock is observed and downstream impairment remains bounded.", mechanism: "Policy and balance-sheet absorption", stateVariables: ["Credit availability", "Settlement continuity"] },
        ],
      },
      {
        id: "financial-stress-propagation",
        title: "Stress propagation",
        summary: "A liquidity, collateral, or regulatory shock reaches funding, credit, or settlement faster than it is contained.",
        priorWeight: 20,
        supportingConditions: ["Liquidity weakens as volatility and correlation rise", "Balance-sheet capacity contracts", "Funding or credit deterioration persists"],
        invalidatingConditions: ["Stress remains isolated", "Market depth recovers across independent sources", "Policy response restores functioning before contraction"],
        checkpoints: [
          { offsetDays: 30, kind: "Branch condition", title: "Detect cross-system propagation", detail: "Require transmission from price shock into funding, collateral, credit, or settlement.", mechanism: "Asset-price and collateral feedback", stateVariables: ["Liquidity", "Leverage", "Credit availability"] },
          { offsetDays: 90, kind: "Terminal classification", title: "Resolve propagation", detail: "Classify propagation only when the downstream mechanism and sustained impairment are both evidenced.", mechanism: "Funding and balance-sheet transmission", stateVariables: ["Funding conditions", "Defaults", "Settlement continuity"] },
        ],
      },
      {
        id: "financial-model-gap",
        title: "Other / model gap",
        summary: "The terminal state is ambiguous or governed by a mechanism outside the declared financial-regime partition.",
        priorWeight: 10,
        modelGap: true,
        supportingConditions: commonGapConditions,
        invalidatingConditions: commonGapInvalidators,
        checkpoints: [{ offsetDays: 90, kind: "Terminal classification", title: "Preserve unmodeled probability mass", detail: "Do not force an ambiguous terminal state into one of the named regimes.", mechanism: "Open-world model boundary", stateVariables: ["Unclassified state", "Missing mechanism"] }],
      },
    ],
    limitations: ["Directional regime evidence is not yet classified from raw source events.", "Bank, credit, collateral, and settlement coverage remains incomplete.", "The output is a baseline scenario distribution pending point-in-time calibration."],
  },
  {
    id: "institutional-rule-transition",
    name: "Institutional rule transition",
    kind: "Institutional transition",
    version: "0.1.0",
    purpose: "Classify the dominant legal and regulatory operating regime affecting digital-asset markets at 180 days.",
    target: "Dominant digital-asset rule regime at day 180",
    partitionRule: "Terminal regimes are defined by the dominant operative constraint; ambiguity enters the model-gap outcome.",
    horizonDays: 180,
    timeStep: "1 day",
    particleCount: 10_000,
    method: "Institutional-state ensemble over rule formation, adjudication, enforcement, and adaptation.",
    equation: "Rₜ₊₁ = f(Rₜ, filings, rulings, enforcement, adaptation) + ε",
    systemIds: ["legal", "financial", "united-states"],
    requiredSourceIds: ["sec", "cftc", "coinbase-global", "coinbase", "kalshi", "polymarket"],
    sourceGaps: ["Federal court dockets and congressional records", "State regulation and compliance implementation data"],
    stateVariables: ["Rule status", "Case status", "Enforcement intensity", "Market access", "Compliance burden"],
    parameters: [
      { symbol: "π", label: "Regime prior", value: "Declared hypothesis weights", provenance: "fixed baseline" },
      { symbol: "g", label: "Epistemic gap", value: "Input-coverage conditioned", provenance: "derived" },
    ],
    sharedCheckpoints: [{ offsetDays: 14, kind: "State estimate", title: "Register operative legal events", detail: "Separate commentary from filings, rules, orders, judgments, enforcement actions, and enforceable deadlines.", mechanism: "Rule formation and adjudication", stateVariables: ["Rule status", "Case status", "Enforcement intensity"] }],
    outcomes: [
      {
        id: "legal-regulatory-acceleration",
        title: "Regulatory acceleration",
        summary: "An operative rule, order, judgment, or enforcement sequence materially changes market constraints.",
        priorWeight: 25,
        supportingConditions: ["A formal legal event changes constraints", "Affected institutions disclose operational responses", "Market structure changes persist"],
        invalidatingConditions: ["Announcements never become operative", "Review or jurisdiction prevents implementation", "Institutional behavior does not change"],
        checkpoints: [
          { offsetDays: 60, kind: "Branch condition", title: "Trace implementation", detail: "Test whether operative legal events change access, disclosure, product availability, or compliance burden.", mechanism: "Compliance transmission", stateVariables: ["Compliance burden", "Market access"] },
          { offsetDays: 180, kind: "Terminal classification", title: "Verify accelerated constraint change", detail: "Require both operative legal status and durable downstream adaptation.", mechanism: "Institutional adaptation", stateVariables: ["Rule status", "Institutional behavior"] },
        ],
      },
      {
        id: "incremental-regulatory-adaptation",
        title: "Incremental adaptation",
        summary: "Rules and institutional behavior change through bounded, sequential adjustments without a regime break.",
        priorWeight: 35,
        supportingConditions: ["Multiple bounded changes become operative", "Institutions adapt without material market-access discontinuity"],
        invalidatingConditions: ["A single change causes a dominant regime break", "No operative changes occur"],
        checkpoints: [
          { offsetDays: 60, kind: "Branch condition", title: "Measure bounded implementation", detail: "Trace discrete compliance and product changes without assuming they constitute acceleration.", mechanism: "Sequential rule adaptation", stateVariables: ["Compliance burden", "Product availability"] },
          { offsetDays: 180, kind: "Terminal classification", title: "Resolve incremental adaptation", detail: "Classify when accumulated operative changes remain bounded and sequential.", mechanism: "Institutional learning", stateVariables: ["Rule status", "Institutional behavior"] },
        ],
      },
      {
        id: "delayed-regulatory-status-quo",
        title: "Delay / status quo",
        summary: "Review, jurisdiction, implementation delay, or inaction leaves the dominant operating constraints materially unchanged.",
        priorWeight: 30,
        supportingConditions: ["Material proposals remain non-operative", "Review or jurisdiction delays implementation", "Market access remains materially unchanged"],
        invalidatingConditions: ["A durable operative constraint changes", "Institutional behavior materially adapts"],
        checkpoints: [
          { offsetDays: 60, kind: "Branch condition", title: "Test legal progression", detail: "Measure whether proposals and cases advance into enforceable constraints.", mechanism: "Legal process delay", stateVariables: ["Case status", "Rule status"] },
          { offsetDays: 180, kind: "Terminal classification", title: "Resolve delay or continuity", detail: "Classify only when the dominant operating boundary remains materially unchanged.", mechanism: "Institutional continuity", stateVariables: ["Market access", "Compliance burden"] },
        ],
      },
      {
        id: "legal-model-gap",
        title: "Other / model gap",
        summary: "The terminal legal state is ambiguous or governed by a mechanism outside the declared rule partition.",
        priorWeight: 10,
        modelGap: true,
        supportingConditions: commonGapConditions,
        invalidatingConditions: commonGapInvalidators,
        checkpoints: [{ offsetDays: 180, kind: "Terminal classification", title: "Preserve unmodeled probability mass", detail: "Retain unresolved jurisdictional or institutional states rather than forcing a named regime.", mechanism: "Open-world model boundary", stateVariables: ["Unclassified state", "Missing mechanism"] }],
      },
    ],
    limitations: ["Legal events are registered but not yet classified automatically by operative effect.", "Court, legislative, and state-level coverage is incomplete.", "The output is uncalibrated and cannot establish trade edge."],
  },
  {
    id: "strategic-interaction-network",
    name: "Strategic interaction network",
    kind: "Interaction network",
    version: "0.1.0",
    purpose: "Classify the dominant cross-polity interaction regime among the United States, China, Russia, and Europe at 180 days.",
    target: "Dominant cross-polity interaction regime at day 180",
    partitionRule: "The dominant material interaction regime determines classification; ambiguous or omitted mechanisms enter model gap.",
    horizonDays: 180,
    timeStep: "1 day",
    particleCount: 10_000,
    method: "Categorical network-regime ensemble with explicit open-world probability mass.",
    equation: "xᵢ,ₜ₊₁ = f(xᵢ,ₜ, Σⱼ Aᵢⱼ actionsⱼ, constraints) + εᵢ",
    systemIds: ["security-defense", "strategic-interaction", "united-states", "china", "russia", "europe"],
    requiredSourceIds: ["eia", "fred", "bls", "sec"],
    sourceGaps: ["Balanced official, legislative, military, and trade coverage for every polity", "Sanctions, export-control, shipping, cyber, and readiness telemetry"],
    stateVariables: ["Threat perception", "Policy posture", "Trade flows", "Technology access", "Escalation pressure"],
    parameters: [
      { symbol: "A", label: "Interaction matrix", value: "Declared system dependencies", provenance: "fixed baseline" },
      { symbol: "g", label: "Epistemic gap", value: "Input-coverage conditioned", provenance: "derived" },
    ],
    sharedCheckpoints: [{ offsetDays: 14, kind: "State estimate", title: "Build balanced cross-polity state", detail: "Require comparable observations across affected polities before interpreting an interaction regime.", mechanism: "Signal, interpretation, and strategic response", stateVariables: ["Threat perception", "Policy posture", "Source balance"] }],
    outcomes: [
      {
        id: "managed-competition",
        title: "Managed competition",
        summary: "Competitive systems preserve enough communication and constraint to prevent a broader cross-system break.",
        priorWeight: 45,
        supportingConditions: ["Communication survives material disputes", "Competitive actions remain bounded", "Spillovers remain contained"],
        invalidatingConditions: ["Communication fails during a material incident", "Retaliation crosses multiple systems", "Commitments repeatedly fail"],
        checkpoints: [
          { offsetDays: 60, kind: "Branch condition", title: "Test containment under pressure", detail: "Observe whether restraint mechanisms operate during a material dispute rather than inferring stability from silence.", mechanism: "Deterrence and bounded competition", stateVariables: ["Escalation pressure", "Strategic restraint"] },
          { offsetDays: 180, kind: "Terminal classification", title: "Resolve managed competition", detail: "Compare commitments with actions and require contained material spillover.", mechanism: "Commitment, action, and feedback", stateVariables: ["Agreement compliance", "Cross-system stability"] },
        ],
      },
      {
        id: "selective-fragmentation",
        title: "Selective fragmentation",
        summary: "Constraints intensify in one or two material domains while broader interaction remains bounded.",
        priorWeight: 30,
        supportingConditions: ["Material restrictions reach a bounded set of flows", "Adaptation remains domain-specific", "Broader escalation is contained"],
        invalidatingConditions: ["Restrictions remain rhetorical", "Fragmentation propagates across most modeled domains"],
        checkpoints: [
          { offsetDays: 60, kind: "Branch condition", title: "Trace domain-specific constraints", detail: "Measure whether policy or security actions alter selected trade, technology, energy, or capital flows.", mechanism: "Selective constraint propagation", stateVariables: ["Trade flows", "Technology access"] },
          { offsetDays: 180, kind: "Terminal classification", title: "Resolve selective fragmentation", detail: "Classify when material fragmentation remains bounded to defined domains.", mechanism: "Bounded adaptation", stateVariables: ["Economic exposure", "Strategic alignment"] },
        ],
      },
      {
        id: "strategic-fragmentation",
        title: "Broad fragmentation",
        summary: "Security competition produces reinforcing restrictions across trade, technology, capital, energy, or readiness systems.",
        priorWeight: 15,
        supportingConditions: ["New constraints restrict multiple cross-border flows", "Actors materially adapt", "Retaliation reinforces the constraint cycle"],
        invalidatingConditions: ["Constraints reverse or remain rhetorical", "Material flows remain unchanged", "Cooperation interrupts escalation"],
        checkpoints: [
          { offsetDays: 60, kind: "Branch condition", title: "Trace cross-domain propagation", detail: "Require transmission from policy or security action into multiple material flow domains.", mechanism: "Cross-system constraint propagation", stateVariables: ["Trade flows", "Energy flows", "Technology access"] },
          { offsetDays: 180, kind: "Terminal classification", title: "Verify reinforcing fragmentation", detail: "Require both initiating constraints and adaptive or retaliatory propagation.", mechanism: "Action, adaptation, and feedback", stateVariables: ["Strategic alignment", "Escalation pressure"] },
        ],
      },
      {
        id: "strategic-model-gap",
        title: "Other / model gap",
        summary: "The terminal interaction state is ambiguous or governed by a mechanism outside the declared network partition.",
        priorWeight: 10,
        modelGap: true,
        supportingConditions: commonGapConditions,
        invalidatingConditions: commonGapInvalidators,
        checkpoints: [{ offsetDays: 180, kind: "Terminal classification", title: "Preserve unmodeled probability mass", detail: "Keep ambiguous compound states outside named regimes until the model boundary is revised.", mechanism: "Open-world model boundary", stateVariables: ["Unclassified state", "Missing mechanism"] }],
      },
    ],
    limitations: ["Cross-polity source coverage is not yet balanced.", "The interaction matrix is structural, not empirically fitted.", "The output is a baseline scenario distribution pending point-in-time validation."],
  },
];

const readiness: Record<ConnectorStatus, number> = {
  live: 1,
  registered: 0.45,
  credentials_required: 0.2,
  unavailable: 0,
};

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function average(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

function hashSeed(value: string) {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function randomGenerator(seed: number) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function normal(random: () => number) {
  const first = Math.max(random(), Number.EPSILON);
  const second = random();
  return Math.sqrt(-2 * Math.log(first)) * Math.cos(2 * Math.PI * second);
}

function parseNumber(value: string) {
  const parsed = Number(value.replace(/[^0-9.+-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function probabilityBasisPoints(counts: number[], total: number) {
  const raw = counts.map((count) => count * 10_000 / total);
  const result = raw.map(Math.floor);
  const remaining = 10_000 - result.reduce((sum, value) => sum + value, 0);
  const order = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index);
  for (let index = 0; index < remaining; index += 1) result[order[index].index] += 1;
  return result;
}

function quantile(sorted: number[], probability: number) {
  if (!sorted.length) return 0;
  const index = (sorted.length - 1) * probability;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

function modelCoverage(
  model: SimulationModel,
  connectors: ConnectorSnapshot[],
  events: TimelineEvidenceEvent[],
) {
  const connectorById = new Map(connectors.map((connector) => [connector.id, connector]));
  const sourceCoverage = average(model.requiredSourceIds.map((id) => {
    const connector = connectorById.get(id);
    return connector ? readiness[connector.status] : 0;
  }));
  const observedIds = new Set(events.flatMap((event) => event.sourceIds));
  const observedCoverage = model.requiredSourceIds.length
    ? model.requiredSourceIds.filter((id) => observedIds.has(id)).length / model.requiredSourceIds.length
    : 0;
  const systemCoverage = average(model.systemIds.map((id) => {
    const system = systems.find((candidate) => candidate.id === id);
    return system?.coverage === "partially linked" ? 0.6 : 0.15;
  }));
  return clamp(sourceCoverage * 0.6 + observedCoverage * 0.2 + systemCoverage * 0.2, 0, 1);
}

function baseRun(
  model: SimulationModel,
  connectors: ConnectorSnapshot[],
  events: TimelineEvidenceEvent[],
  asOf: string | null,
  revision: number,
) {
  const inputCoverage = modelCoverage(model, connectors, events);
  const connectorById = new Map(connectors.map((connector) => [connector.id, connector]));
  const seedMaterial = [
    model.id,
    model.version,
    asOf ?? "unanchored",
    revision,
    ...model.requiredSourceIds.map((id) => {
      const connector = connectorById.get(id);
      return `${id}:${connector?.status ?? "missing"}:${connector?.primary.value ?? ""}:${connector?.secondary.value ?? ""}`;
    }),
  ].join("|");
  const seed = hashSeed(seedMaterial);
  return {
    id: `${model.id}:${model.version}:${seed.toString(16).padStart(8, "0")}`,
    modelId: model.id,
    modelVersion: model.version,
    asOf,
    revision,
    seed,
    status: inputCoverage >= 0.4 ? "completed" as const : "input limited" as const,
    particleCount: model.particleCount,
    inputCoverage: Math.round(inputCoverage * 100),
    modelConfidence: Math.min(55, Math.round(inputCoverage * 55)),
    calibration: {
      status: "pending" as const,
      outOfSampleForecasts: 0 as const,
      brierScore: null,
      logLoss: null,
    },
  };
}

function runCategoricalModel(
  model: SimulationModel,
  connectors: ConnectorSnapshot[],
  events: TimelineEvidenceEvent[],
  asOf: string | null,
  revision: number,
): SimulationRun {
  const run = baseRun(model, connectors, events, asOf, revision);
  const random = randomGenerator(run.seed);
  const inputCoverage = run.inputCoverage / 100;
  const gapMass = clamp(0.08 + (1 - inputCoverage) * 0.34, 0.08, 0.42);
  const regularOutcomes = model.outcomes.filter((outcome) => !outcome.modelGap);
  const regularWeight = regularOutcomes.reduce((sum, outcome) => sum + outcome.priorWeight, 0);
  const probabilities = model.outcomes.map((outcome) => outcome.modelGap
    ? gapMass
    : (1 - gapMass) * outcome.priorWeight / regularWeight);
  const cumulative = probabilities.reduce<number[]>((values, probability) => {
    values.push((values.at(-1) ?? 0) + probability);
    return values;
  }, []);
  const counts = new Array(model.outcomes.length).fill(0) as number[];
  for (let particle = 0; particle < model.particleCount; particle += 1) {
    const draw = random();
    const outcomeIndex = cumulative.findIndex((boundary) => draw <= boundary);
    counts[outcomeIndex === -1 ? counts.length - 1 : outcomeIndex] += 1;
  }
  const basisPoints = probabilityBasisPoints(counts, model.particleCount);

  return {
    ...run,
    inputs: [
      { label: "Required-source coverage", value: `${run.inputCoverage}%` },
      { label: "Explicit model-gap mass", value: `${(gapMass * 100).toFixed(1)}%` },
    ],
    outcomes: model.outcomes.map((outcome, index) => ({
      id: outcome.id,
      title: outcome.title,
      sampleCount: counts[index],
      probabilityBps: basisPoints[index],
    })),
    metrics: [
      { label: "Input coverage", value: run.inputCoverage, unit: "%" },
      { label: "Model gap", value: gapMass * 100, unit: "%" },
    ],
  };
}

function runBtcModel(
  model: SimulationModel,
  connectors: ConnectorSnapshot[],
  events: TimelineEvidenceEvent[],
  asOf: string | null,
  revision: number,
): SimulationRun {
  const run = baseRun(model, connectors, events, asOf, revision);
  const connectorById = new Map(connectors.map((connector) => [connector.id, connector]));
  const coinbase = connectorById.get("coinbase");
  const binance = connectorById.get("binance");
  const coinbaseSpot = coinbase?.status === "live" ? parseNumber(coinbase.primary.value) : null;
  const binanceBid = binance?.status === "live" ? parseNumber(binance.primary.value) : null;
  const binanceAsk = binance?.status === "live" ? parseNumber(binance.secondary.value) : null;
  const binanceMid = binanceBid !== null && binanceAsk !== null ? (binanceBid + binanceAsk) / 2 : null;
  const spotValues = [coinbaseSpot, binanceMid].filter((value): value is number => value !== null);
  const spot = spotValues.length ? average(spotValues) : null;
  const dailyChange = coinbase?.status === "live" ? parseNumber(coinbase.secondary.value) : null;
  const annualDrift = clamp((dailyChange ?? 0) / 100 * 12, -0.3, 0.3);
  const annualVolatility = 0.65;
  const jumpProbability = 0.02;
  const jumpDispersion = 0.08;
  const dayFraction = 1 / 365;
  const random = randomGenerator(run.seed);
  const returns: number[] = [];
  const counts = new Array(model.outcomes.length).fill(0) as number[];

  for (let particle = 0; particle < model.particleCount; particle += 1) {
    let logReturn = 0;
    for (let day = 0; day < model.horizonDays; day += 1) {
      const diffusion = annualVolatility * Math.sqrt(dayFraction) * normal(random);
      const jump = random() < jumpProbability ? jumpDispersion * normal(random) : 0;
      logReturn += (annualDrift - annualVolatility ** 2 / 2) * dayFraction + diffusion + jump;
    }
    const terminalReturn = (Math.exp(logReturn) - 1) * 100;
    returns.push(terminalReturn);
    const outcomeIndex = terminalReturn <= -20
      ? 0
      : terminalReturn < -5
        ? 1
        : terminalReturn <= 5
          ? 2
          : terminalReturn <= 20
            ? 3
            : 4;
    counts[outcomeIndex] += 1;
  }

  returns.sort((left, right) => left - right);
  const basisPoints = probabilityBasisPoints(counts, model.particleCount);
  const lossCount = returns.filter((value) => value < 0).length;
  const tailCount = Math.max(1, Math.floor(returns.length * 0.05));
  const expectedShortfall = average(returns.slice(0, tailCount));
  const meanReturn = average(returns);

  return {
    ...run,
    modelConfidence: Math.min(45, run.modelConfidence),
    histogram: buildReturnHistogram(returns),
    inputs: [
      { label: "Spot reference", value: spot === null ? "Unavailable" : `$${spot.toLocaleString("en-US", { maximumFractionDigits: 2 })}`, sourceId: "coinbase" },
      { label: "24h conditioning return", value: dailyChange === null ? "Unavailable · neutral drift" : `${dailyChange >= 0 ? "+" : ""}${dailyChange.toFixed(2)}%`, sourceId: "coinbase" },
      { label: "Annual drift cap", value: `${annualDrift >= 0 ? "+" : ""}${(annualDrift * 100).toFixed(1)}%` },
      { label: "Annual volatility baseline", value: `${(annualVolatility * 100).toFixed(0)}%` },
    ],
    outcomes: model.outcomes.map((outcome, index) => ({
      id: outcome.id,
      title: outcome.title,
      sampleCount: counts[index],
      probabilityBps: basisPoints[index],
    })),
    metrics: [
      { label: "Mean return", value: meanReturn, unit: "%" },
      { label: "Median return", value: quantile(returns, 0.5), unit: "%" },
      { label: "Loss probability", value: lossCount / returns.length * 100, unit: "%" },
      { label: "5% expected shortfall", value: expectedShortfall, unit: "%" },
      { label: "5th percentile", value: quantile(returns, 0.05), unit: "%" },
      { label: "95th percentile", value: quantile(returns, 0.95), unit: "%" },
    ],
  };
}

export function buildReturnHistogram(returns: number[], binCount = 48) {
  if (!Number.isInteger(binCount) || binCount < 1 || binCount > 200) throw new RangeError("Histogram bin count must be between 1 and 200.");
  const values = returns.filter(Number.isFinite);
  if (!values.length) return [];
  const lower = Math.min(...values);
  const upper = Math.max(...values);
  const step = (upper - lower || 1) / binCount;
  const bins = Array.from({ length: binCount }, (_, index) => ({ lower: lower + index * step, upper: lower + (index + 1) * step, count: 0 }));
  values.forEach(value => { bins[Math.min(binCount - 1, Math.floor((value - lower) / step))].count += 1; });
  return bins;
}

export function runSimulationModels(
  connectors: ConnectorSnapshot[],
  events: TimelineEvidenceEvent[] = [],
  options: { asOf?: string | null; revision?: number } = {},
) {
  const asOf = options.asOf ?? null;
  const revision = options.revision ?? 0;
  return simulationModels.map((model) => model.id === "btc-jump-diffusion"
    ? runBtcModel(model, connectors, events, asOf, revision)
    : runCategoricalModel(model, connectors, events, asOf, revision));
}

export function getSimulationModel(id: string) {
  return simulationModels.find((model) => model.id === id) ?? null;
}

export function getSimulationRun(runs: SimulationRun[], id: string) {
  return runs.find((run) => run.id === id || run.modelId === id) ?? null;
}
